import React, { useState, useEffect } from 'react';
import { Modal, Badge, ProgressBar, Button, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faEnvelope,
  faPhone,
  faChartLine,
  faCheckCircle,
  faClock,
  faExclamationCircle,
  faPlay,
  faPause,
  faCalendar,
  faBolt
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

interface LeadWorkflowDetailsProps {
  show: boolean;
  leadId: string;
  leadName?: string;
  onHide: () => void;
}

interface WorkflowTask {
  task_id: string;
  agent_type: string;
  action: string;
  priority: string;
  scheduled_for: string;
  status: string;
  requires_approval: boolean;
  task_data?: any;
  ai_response?: any;
  created_at: string;
  executed_at?: string;
  completed_at?: string;
  error_message?: string;
}

interface WorkflowData {
  workflow: {
    instance_id: string;
    lead_id: string;
    workflow_template_id: string;
    status: string;
    automation_level: string;
    metadata?: any;
    created_at: string;
    lead_first_name: string;
    lead_last_name: string;
    lead_email: string;
    lead_score: number;
  };
  tasks: WorkflowTask[];
  metrics: {
    total_tasks: number;
    completed_tasks: number;
    pending_tasks: number;
    failed_tasks: number;
    progress_percentage: number;
  };
}

const LeadWorkflowDetails: React.FC<LeadWorkflowDetailsProps> = ({
  show,
  leadId,
  leadName,
  onHide
}) => {
  const [workflowData, setWorkflowData] = useState<WorkflowData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show && leadId) {
      fetchWorkflowDetails();
    }
  }, [show, leadId]);

  const fetchWorkflowDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.get(`/agent-kairos/leads/${leadId}/workflow`);
      
      if (response.data.success && response.data.data) {
        setWorkflowData(response.data.data);
      } else {
        setWorkflowData(null);
        if (response.data.message) {
          setError(response.data.message);
        }
      }
    } catch (err) {
      console.error('Error fetching workflow details:', err);
      setError('Failed to load workflow details');
      setWorkflowData(null);
    } finally {
      setLoading(false);
    }
  };

  const getAgentIcon = (agentType: string) => {
    switch (agentType) {
      case 'response': return faEnvelope;
      case 'intelligence': return faChartLine;
      case 'nurture': return faRobot;
      case 'monitor': return faChartLine;
      default: return faRobot;
    }
  };

  const getActionLabel = (action: string) => {
    const labels: { [key: string]: string } = {
      'send_reengagement_email': 'Re-engagement Email',
      'analyze_property_matches': 'Property Analysis',
      'send_property_matches': 'Property Matches Email',
      'schedule_consultation': 'Schedule Consultation',
      'send_market_update': 'Market Update Email',
      'send_sms_followup': 'SMS Follow-up',
      'create_task_reminder': 'Task Reminder',
      'update_lead_score': 'Update Lead Score'
    };
    return labels[action] || action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return faCheckCircle;
      case 'pending': return faClock;
      case 'executing': return faPlay;
      case 'failed': return faExclamationCircle;
      default: return faClock;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#22c55e';
      case 'pending': return '#f59e0b';
      case 'executing': return '#00d4ff';
      case 'failed': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const handlePauseWorkflow = async () => {
    if (!workflowData?.workflow.instance_id) return;
    
    try {
      await axiosInstance.post(`/agent-kairos/workflows/${workflowData.workflow.instance_id}/pause`);
      toast.success('Workflow paused');
      fetchWorkflowDetails();
    } catch (error) {
      toast.error('Failed to pause workflow');
    }
  };

  const handleResumeWorkflow = async () => {
    if (!workflowData?.workflow.instance_id) return;
    
    try {
      await axiosInstance.post(`/agent-kairos/workflows/${workflowData.workflow.instance_id}/resume`);
      toast.success('Workflow resumed');
      fetchWorkflowDetails();
    } catch (error) {
      toast.error('Failed to resume workflow');
    }
  };

  const handleApproveTask = async (taskId: string) => {
    try {
      await axiosInstance.post(`/agent-kairos/tasks/${taskId}/approve`);
      toast.success('Task approved for execution');
      fetchWorkflowDetails();
    } catch (error) {
      toast.error('Failed to approve task');
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton style={{ background: '#f8f9fa' }}>
        <Modal.Title>
          <FontAwesomeIcon icon={faBolt} className="me-2" style={{ color: '#00d4ff' }} />
          Workflow Automation Details
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: '#00d4ff' }} />
            <div className="mt-3">Loading workflow details...</div>
          </div>
        )}

        {error && !loading && (
          <Alert variant="info">
            <FontAwesomeIcon icon={faRobot} className="me-2" />
            {error}
          </Alert>
        )}

        {!loading && !error && workflowData && (
          <div>
            {/* Lead Info Header */}
            <div className="mb-4 p-3 bg-light rounded">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h5 className="mb-1">
                    {leadName || `${workflowData.workflow.lead_first_name} ${workflowData.workflow.lead_last_name}`}
                  </h5>
                  <div className="text-muted">
                    {workflowData.workflow.lead_email}
                  </div>
                  <div className="mt-2">
                    <Badge bg="light" text="dark" className="me-2">
                      Score: {workflowData.workflow.lead_score}
                    </Badge>
                    <Badge 
                      bg={workflowData.workflow.status === 'ACTIVE' ? 'success' : 
                         workflowData.workflow.status === 'PAUSED' ? 'warning' : 'secondary'}
                    >
                      {workflowData.workflow.status}
                    </Badge>
                    <Badge bg="info" className="ms-2">
                      {workflowData.workflow.automation_level}
                    </Badge>
                  </div>
                </div>
                <div>
                  {workflowData.workflow.status === 'ACTIVE' ? (
                    <Button 
                      size="sm" 
                      variant="warning"
                      onClick={handlePauseWorkflow}
                    >
                      <FontAwesomeIcon icon={faPause} className="me-1" />
                      Pause
                    </Button>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="success"
                      onClick={handleResumeWorkflow}
                    >
                      <FontAwesomeIcon icon={faPlay} className="me-1" />
                      Resume
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Progress Overview */}
            <div className="mb-4">
              <h6 className="mb-3">Automation Progress</h6>
              <ProgressBar className="mb-2" style={{ height: '25px' }}>
                <ProgressBar 
                  variant="success" 
                  now={workflowData.metrics.progress_percentage}
                  label={`${workflowData.metrics.progress_percentage}%`}
                />
              </ProgressBar>
              <div className="d-flex justify-content-between text-muted small">
                <span>{workflowData.metrics.completed_tasks} completed</span>
                <span>{workflowData.metrics.pending_tasks} pending</span>
                <span>{workflowData.metrics.failed_tasks} failed</span>
              </div>
            </div>

            {/* Task Timeline */}
            <div>
              <h6 className="mb-3">Automation Timeline</h6>
              <div className="task-timeline">
                {workflowData.tasks.map((task, index) => (
                  <div key={task.task_id} className={`d-flex align-items-start mb-3 ${index < workflowData.tasks.length - 1 ? 'pb-3 border-start border-2 ms-2 ps-4' : 'ps-4 ms-2'}`}
                    style={{ 
                      borderColor: index < workflowData.tasks.length - 1 ? '#e5e7eb' : 'transparent',
                      position: 'relative' 
                    }}
                  >
                    {/* Timeline Dot */}
                    <div 
                      style={{
                        position: 'absolute',
                        left: '-8px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(task.status),
                        border: '3px solid white',
                        boxShadow: '0 0 0 1px #e5e7eb'
                      }}
                    />
                    
                    {/* Task Content */}
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <FontAwesomeIcon 
                              icon={getAgentIcon(task.agent_type)} 
                              style={{ color: '#00d4ff' }}
                            />
                            <strong>{getActionLabel(task.action)}</strong>
                            <Badge bg={task.priority === 'high' ? 'danger' : 'secondary'} className="ms-2">
                              {task.priority}
                            </Badge>
                          </div>
                          <div className="text-muted small">
                            <FontAwesomeIcon icon={faCalendar} className="me-1" />
                            Scheduled: {format(new Date(task.scheduled_for), 'MMM dd, yyyy h:mm a')}
                          </div>
                          {task.completed_at && (
                            <div className="text-success small">
                              Completed: {format(new Date(task.completed_at), 'MMM dd, yyyy h:mm a')}
                            </div>
                          )}
                          {task.error_message && (
                            <Alert variant="danger" className="mt-2 mb-0 py-1 px-2 small">
                              {task.error_message}
                            </Alert>
                          )}
                        </div>
                        <div>
                          <FontAwesomeIcon 
                            icon={getStatusIcon(task.status)}
                            style={{ color: getStatusColor(task.status), fontSize: '1.2rem' }}
                          />
                          {task.requires_approval && task.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline-success"
                              className="ms-2"
                              onClick={() => handleApproveTask(task.task_id)}
                            >
                              Approve
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LeadWorkflowDetails;